import * as Tooltip from '@radix-ui/react-tooltip';
import { Info } from 'lucide-react';
import React from 'react';
import { Button } from '../../components/ui/button';
import type { VoiceTenantCallAggregate } from '@reach/shared-types/voice-agent-calls';
import { VoiceTenantRow } from './VoiceTenantRow';
import styles from './VoiceTenantsTable.module.css';

interface VoiceTenantsTableProps {
  data: VoiceTenantCallAggregate[];
  isLoading: boolean;
  error: Error | null;
  hasNextPage: boolean;
  currentPage: number;
  isFetching: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

export const VoiceTenantsTable: React.FC<VoiceTenantsTableProps> = ({
  data,
  isLoading,
  error,
  hasNextPage,
  currentPage,
  isFetching,
  onNextPage,
  onPreviousPage,
}) => {
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingPulse}>
          <div className={`${styles.loadingBar} ${styles.loadingBarShort}`} />
          <div className={`${styles.loadingBar} ${styles.loadingBarFull}`} />
          <div className={`${styles.loadingBar} ${styles.loadingBarFull}`} />
          <div className={`${styles.loadingBar} ${styles.loadingBarFull}`} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorTitle}>Error loading voice data</div>
        <div className={styles.errorMessage}>{error.message || 'An unexpected error occurred'}</div>
      </div>
    );
  }

  if (data.length === 0) {
    return <div className={styles.empty}>No voice call data found for your tenants.</div>;
  }

  const startRank = (currentPage - 1) * 25 + 1;

  return (
    <div className={styles.tableWrapper}>
      <div className="overflow-x-auto">
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Tenant Name</th>
              <th className={styles.sortable}>Total Calls</th>
              <th>Transferred</th>
              <th>Duration</th>
              <th>Usage Cost</th>
              <th>
                <Tooltip.Provider delayDuration={200}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <span className={`${styles.thWithTooltip} inline-flex items-center gap-1`}>
                        Sentiment
                        <Info className="h-3 w-3 text-gray-400" />
                      </span>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        side="top"
                        sideOffset={4}
                        className="z-50 max-w-xs px-3 py-2 text-xs font-normal normal-case tracking-normal text-white bg-gray-900 rounded-md shadow-md">
                        AI-analyzed caller sentiment. "Satisfied" = positive + neutral calls as a
                        percentage of all calls with detected sentiment.
                        <Tooltip.Arrow className="fill-gray-900" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((tenant, idx) => (
              <VoiceTenantRow
                key={tenant.tenantExternalId ?? idx}
                rank={startRank + idx}
                tenant={tenant}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <div className={styles.pageInfo}>
          Showing {startRank}–{startRank + data.length - 1}
        </div>
        <div className={styles.pageControls}>
          <Button
            variant="outline"
            size="sm"
            onClick={onPreviousPage}
            disabled={currentPage === 1 || isFetching}>
            Previous
          </Button>
          <span>Page {currentPage}</span>
          <Button variant="outline" size="sm" onClick={onNextPage} disabled={!hasNextPage || isFetching}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};
